<template>
  <div class="table-body">
    <div v-if="!layerSelected" class="empty-selection">
      Seleccione una Infraestructura Vulnerable
    </div>
    <div v-else-if="!selectedLayerFeatures || selectedLayerFeatures.length === 0" class="empty-selection">
      No hay datos disponibles para esta capa.
    </div>
    <div v-else class="table-container">
      <div class="table-actions">
        <div class="column-toggler-container" v-click-outside="closeColumnMenu">
          <button class="btn btn-sm btn-outline-secondary" @click="showColumnMenu = !showColumnMenu" title="Mostrar/Ocultar columnas">
            <font-awesome-icon icon="list-alt" /> Columnas
          </button>
          <div v-if="showColumnMenu" class="column-dropdown shadow-sm">
            <div class="column-dropdown-header">
              Columnas visibles
            </div>
            <div class="column-dropdown-body">
              <label class="column-checkbox" style="font-weight: bold; border-bottom: 1px solid #eee; padding-bottom: 5px; margin-bottom: 5px;">
                <input type="checkbox" :checked="areAllColumnsVisible" @change="toggleAllColumns">
                Seleccionar Todas
              </label>
              <label v-for="col in tableColumns" :key="col" class="column-checkbox">
                <input type="checkbox" :value="col" :checked="!hiddenColumns.includes(col)" @change="toggleColumn(col)">
                {{ col }}
              </label>
            </div>
          </div>
        </div>
      </div>
      <div class="table-responsive">
        <table class="table table-striped table-bordered table-hover custom-table">
          <thead>
            <tr>
              <th v-for="col in visibleColumns" :key="col">{{ col }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, idx) in selectedLayerFeatures" :key="idx" @click="zoomToFeature(idx)" class="clickable-row" title="Clic para centrar en el mapa">
              <td v-for="col in visibleColumns" :key="col">{{ row[col] || '-' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { mapService } from '@/services/map/mapService.js';
import { fromLonLat } from 'ol/proj';

export default {
  name: "GeoprocessingTable",
  props: {
    layerSelected: {
      type: [String, Number],
      default: ""
    },
    resultsTable: {
      type: Object,
      default: () => ({})
    },
    rawResults: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      hiddenColumns: [],
      showColumnMenu: false
    }
  },
  computed: {
    selectedLayerFeatures() {
      if (!this.layerSelected || !this.resultsTable) return [];
      return this.resultsTable[this.layerSelected] || [];
    },
    tableColumns() {
      if (!this.selectedLayerFeatures || this.selectedLayerFeatures.length === 0) return [];
      const keys = new Set();
      this.selectedLayerFeatures.forEach(row => {
          Object.keys(row).forEach(k => keys.add(k));
      });
      const excludedKeys = ["FID", "OBJECTID", "Shape", "geom", "the_geom", "layer_id", "layer_name", "geometry"];
      return Array.from(keys).filter(key => !excludedKeys.includes(key));
    },
    visibleColumns() {
      return this.tableColumns.filter(col => !this.hiddenColumns.includes(col));
    },
    areAllColumnsVisible() {
      return this.hiddenColumns.length === 0;
    }
  },
  watch: {
    layerSelected() {
      this.hiddenColumns = [];
      this.showColumnMenu = false;
    }
  },
  methods: {
    toggleColumn(col) {
      const idx = this.hiddenColumns.indexOf(col);
      if (idx === -1) {
        this.hiddenColumns.push(col);
      } else {
        this.hiddenColumns.splice(idx, 1);
      }
    },
    toggleAllColumns() {
      if (this.areAllColumnsVisible) {
        this.hiddenColumns = [...this.tableColumns];
      } else {
        this.hiddenColumns = [];
      }
    },
    closeColumnMenu() {
      this.showColumnMenu = false;
    },
    async zoomToFeature(idx) {
      if (!this.rawResults || !this.rawResults.length) return;
      const layerData = this.rawResults.find(r => String(r.layer.id) === String(this.layerSelected));
      if (!layerData) return;
      
      const layerRes = layerData.layer_results || {};
      const features = layerRes.features || layerRes.data || [];
      const feature = features[idx];
      
      if (!feature) return;
      
      let geometry = feature.geometry;
      
      if (!geometry) {
        const layer = layerData.layer;
        const attrs = feature.attributes || feature;
        const featureId = attrs.objectid !== undefined ? attrs.objectid : 
                         (attrs.OBJECTID !== undefined ? attrs.OBJECTID : 
                         (attrs.FID !== undefined ? attrs.FID : attrs.fid));
                         
        if (layer && layer.provider && layer.provider.name === "arcgis" && featureId !== undefined) {
            const baseUrl = layer.provider.url;
            const layerCodigo = layer.codigo || layer.id;
            let url = `${baseUrl}/${layerCodigo}/query`;
            if (baseUrl.toLowerCase().includes("imageserver")) {
                url = `${baseUrl}/query`;
            }
            
            const idField = attrs.objectid !== undefined ? 'objectid' : 
                           (attrs.OBJECTID !== undefined ? 'OBJECTID' : 
                           (attrs.FID !== undefined ? 'FID' : 'fid'));

            const params = new URLSearchParams({
                where: `${idField}=${featureId}`,
                returnGeometry: 'true',
                outFields: idField,
                outSR: '3857',
                f: 'json'
            });

            try {
                const response = await fetch(`${url}?${params.toString()}`).then(res => res.json());
                if (response.features && response.features.length > 0) {
                    geometry = response.features[0].geometry;
                }
            } catch (err) {
                console.error("Error fetching feature geometry:", err);
            }
        }
      }
      
      if (!geometry) return;
      
      const map = mapService.get();
      if (!map) return;
      
      if (geometry.rings || geometry.paths) {
        const shapeArray = geometry.rings || geometry.paths;
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        shapeArray.forEach(ring => {
            ring.forEach(p => {
              if (p[0] < minX) minX = p[0];
              if (p[1] < minY) minY = p[1];
              if (p[0] > maxX) maxX = p[0];
              if (p[1] > maxY) maxY = p[1];
            });
        });
        
        const isLatLon = minX >= -180 && minX <= 180 && minY >= -90 && minY <= 90;
        let extent = [minX, minY, maxX, maxY];
        
        if (isLatLon) {
          const minP = fromLonLat([minX, minY]);
          const maxP = fromLonLat([maxX, maxY]);
          extent = [minP[0], minP[1], maxP[0], maxP[1]];
        }
        
        map.getView().fit(extent, { duration: 1000, padding: [100, 100, 100, 100], maxZoom: 18 });
      } else if (geometry.x && geometry.y) {
        const isLatLon = geometry.x >= -180 && geometry.x <= 180 && geometry.y >= -90 && geometry.y <= 90;
        let center = [geometry.x, geometry.y];
        
        if (isLatLon) {
          center = fromLonLat(center);
        }
        
        map.getView().animate({ center: center, zoom: 18, duration: 1000 });
      }
    }
  }
};
</script>

<style scoped>
.table-body {
  padding: 1em;
  min-height: 400px;
}

.empty-selection {
  background-color: #dcf2f9;
  color: #31708f;
  padding: 15px;
  border-radius: 4px;
  text-align: center;
  font-weight: 500;
  margin-top: 10px;
  min-height: 380px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
}

.custom-table {
  font-size: 0.85rem;
  background: white;
}

.custom-table {
  width: 100%;
  table-layout: auto;
  border-collapse: collapse;
}

.custom-table th, .custom-table td {
  padding: 8px 12px;
  vertical-align: middle;
  border: 1px solid #ccc !important;
}

.custom-table th {
  font-weight: 600;
  color: #2c3e50;
  background-color: #e9ecef !important;
  border-bottom: 2px solid #999 !important;
}

.custom-table tbody tr:nth-of-type(odd) {
  background-color: #f8f9fa !important;
}

.custom-table tbody tr:nth-of-type(even) {
  background-color: #ffffff !important;
}

.custom-table tbody tr:hover {
  background-color: #e3f2fd !important;
}

.clickable-row {
  cursor: pointer;
  transition: all 0.2s ease;
}

.clickable-row:active {
  background-color: #bbdefb !important;
}

.table-actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
}

.column-toggler-container {
  position: relative;
}

.column-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border: 1px solid #ccc;
  border-radius: 4px;
  z-index: 1000;
  width: max-content;
  max-width: 250px;
  margin-top: 4px;
}

.column-dropdown-header {
  padding: 8px 12px;
  font-weight: bold;
  background: #f8f9fa;
  border-bottom: 1px solid #eee;
  font-size: 0.85em;
  color: #555;
}

.column-dropdown-body {
  max-height: 250px;
  overflow-y: auto;
  padding: 8px;
}

.column-checkbox {
  display: block;
  font-size: 0.85em;
  margin-bottom: 4px;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.column-checkbox input {
  margin-right: 6px;
}

.table-responsive {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #ddd;
  border-radius: 4px;
}
</style>
